import {
    Button,
    DatePickerProvider,
    Divider,
    Icon,
    IconLib,
    Label,
    Menu,
    MenuDivider,
    MenuHeading,
    MenuItem,
    MenuSection,
    ScrollingDatePicker,
    Text,
    Toggle,
    Tooltip,
    useCheck,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import {
    TbCalendar,
    TbCalendarCheck,
    TbCalendarDue,
    TbCalendarOff,
    TbClearAll,
    TbFlag2,
    TbFlag3,
    TbRowInsertBottom,
    TbRowInsertTop,
    TbSun,
    TbSunrise,
} from 'react-icons/tb'
import { Bars, weekdays } from '../components'

export const TaskMenu = (props: any) => {
    const { task, showAddBelow, showAddAbove, onView, onTodoAddBelow, onTodoAddAbove, onDelete, onSave, type } = props
    const dateRange = useCheck(!!task?.end)
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const [selection, setSelection] = useState<any[]>([])
    const { start, end } = useMemo(() => {
        const start = task.start ? new Date(task.start) : new Date()
        const end = task.end ? new Date(task.end) : start
        return { start, end }
    }, [task])

    const handleDatePreset = (preset) => {
        const now = new Date()
        let start = new Date()
        let end = new Date()
        const currentDate = new Date()
        const dayOfWeek = currentDate.getDay()

        switch (preset) {
            case 'today':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
                dateRange.setChecked(false)
                break
            case 'tomorrow':
                const tomorrow = new Date(now)
                tomorrow.setDate(now.getDate() + 1)
                start = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate())
                end = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999)
                dateRange.setChecked(false)
                break
            case 'next-week':
                const daysUntilNextMonday = (8 - dayOfWeek) % 7 || 7
                const nextMonday = new Date(currentDate)
                nextMonday.setDate(currentDate.getDate() + daysUntilNextMonday)
                nextMonday.setHours(0, 0, 0, 0)
                start = nextMonday
                end = nextMonday
                dateRange.setChecked(false)
                break
            case 'next-month':
                const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                start = nextMonth
                end = nextMonth
                break
            case 'none':
                start = null
                end = null
                break
        }

        onSave({ start, end, wholeDay: true })
        setSelection([[start, end]])
    }

    const handleSelection = (date: Date) => {
        if (!dateRange.checked) {
            setSelection([[date, date]])
            onSave({ start: date, end: null, wholeDay: true })
        } else {
            if (selection.length == 0) {
                setSelection([[date, null]])
            } else {
                const selected = selection[0]
                if (!selected[0]) return setSelection([date, null])
                if (!!selected[0] && !!selected[1]) return setSelection([[date, null]])
                if (!!selected[0] && !selected[1]) {
                    const newStart = selected[0] > date ? date : selected[0]
                    const newEnd = selected[0] > date ? selected[0] : date
                    setSelection([[newStart, newEnd]])
                    onSave({ start: newStart, end: newEnd, wholeDay: true })
                    return
                }
            }
        }
    }

    useEffect(() => {
        setSelection([[start, end]])
    }, [start, end])

    return (
        <Menu
            disableAutoFocus
            width={230}>
            <MenuItem
                prefix={<IconLib icon="maximize" />}
                onClick={onView}>
                Open
            </MenuItem>
            {(type == 'project' || type == 'unassigned') && showAddAbove && (
                <MenuItem
                    prefix={<Icon icon={TbRowInsertTop} />}
                    onClick={onTodoAddAbove}>
                    Add Above
                </MenuItem>
            )}
            {(type == 'project' || type == 'unassigned') && showAddBelow && (
                <MenuItem
                    prefix={<Icon icon={TbRowInsertBottom} />}
                    onClick={onTodoAddBelow}>
                    Add Below
                </MenuItem>
            )}
            <MenuItem
                prefix={<IconLib icon="bin" />}
                onClick={onDelete}>
                Delete
            </MenuItem>
            <MenuDivider />
            <MenuHeading>Priority</MenuHeading>
            <MenuSection>
                <View
                    row
                    width="100%"
                    justifyContent="space-between">
                    <Button
                        subtle
                        size="xs"
                        active={task.priority == '' || !task.priority}
                        onClick={() => onSave({ priority: null })}>
                        <Icon
                            icon={TbFlag3}
                            color="var(--f-color-text-weakest)"
                            size="lg"
                        />
                    </Button>
                    <Button
                        subtle
                        variant="caution"
                        size="xs"
                        active={task.priority == 'low'}
                        onClick={() => onSave({ priority: 'low' })}>
                        <Icon
                            icon={TbFlag3}
                            color="var(--f-color-caution)"
                            size="lg"
                        />
                    </Button>
                    <Button
                        subtle
                        variant="warning"
                        size="xs"
                        active={task.priority == 'medium'}
                        onClick={() => onSave({ priority: 'medium' })}>
                        <Icon
                            icon={TbFlag3}
                            color="var(--f-color-warning)"
                            size="lg"
                        />
                    </Button>
                    <Button
                        subtle
                        variant="danger"
                        size="xs"
                        active={task.priority == 'high'}
                        onClick={() => onSave({ priority: 'high' })}>
                        <Icon
                            icon={TbFlag3}
                            color="var(--f-color-text-danger)"
                            size="lg"
                        />
                    </Button>
                </View>
            </MenuSection>
            <MenuDivider />
            <MenuHeading>Complexity</MenuHeading>
            <MenuSection>
                <View
                    row
                    width="100%"
                    justifyContent="space-between">
                    <Button
                        subtle
                        size="xs"
                        bgToken={!task.complexity ? 'surface-strong' : undefined}
                        onClick={() => onSave({ complexity: null })}>
                        <Bars complexity={null} />
                    </Button>
                    <Button
                        subtle
                        size="xs"
                        bgToken={task.complexity == 'low' ? 'surface-strong' : undefined}
                        onClick={() => onSave({ complexity: 'low' })}>
                        <Bars complexity="low" />
                    </Button>
                    <Button
                        subtle
                        size="xs"
                        bgToken={task.complexity == 'medium' ? 'surface-strong' : undefined}
                        onClick={() => onSave({ complexity: 'medium' })}>
                        <Bars complexity="medium" />
                    </Button>
                    <Button
                        subtle
                        size="xs"
                        bgToken={task.complexity == 'high' ? 'surface-strong' : undefined}
                        onClick={() => onSave({ complexity: 'high' })}>
                        <Bars complexity="high" />
                    </Button>
                </View>
            </MenuSection>
            <MenuDivider />
            <MenuHeading>Due date</MenuHeading>
            <MenuSection>
                <View
                    row
                    width="100%"
                    justifyContent="space-between">
                    <Tooltip text="Today">
                        <Button
                            subtle
                            onClick={() => handleDatePreset('today')}>
                            <Icon icon={TbSun} />
                        </Button>
                    </Tooltip>
                    <Tooltip text="Tomorrow">
                        <Button
                            subtle
                            onClick={() => handleDatePreset('tomorrow')}>
                            <Icon icon={TbSunrise} />
                        </Button>
                    </Tooltip>
                    <Tooltip text="Next week">
                        <Button
                            subtle
                            onClick={() => handleDatePreset('next-week')}>
                            <Icon icon={TbCalendar} />
                        </Button>
                    </Tooltip>
                    <Tooltip text="Next month">
                        <Button
                            subtle
                            onClick={() => handleDatePreset('next-week')}>
                            <Icon icon={TbCalendarCheck} />
                        </Button>
                    </Tooltip>
                    <Tooltip text="Clear">
                        <Button
                            subtle
                            onClick={() => handleDatePreset('none')}>
                            <Icon icon={TbCalendarOff} />
                        </Button>
                    </Tooltip>
                </View>
            </MenuSection>
            <MenuDivider />
            <MenuSection p="0">
                <DatePickerProvider>
                    <ScrollingDatePicker
                        height={235}
                        defaultDate={start}
                        selection={selection}
                        onChange={handleSelection}
                        monthTitle={(date: Date) => (
                            <View
                                column
                                colorToken="accent"
                                as="span"
                                width="100%">
                                <span
                                    className="f-text sm f-date-select__month"
                                    style={{ padding: '0.5rem' }}>
                                    {monthNames[date.getMonth()]} {date.getFullYear()}
                                </span>
                                <span
                                    className="f-row"
                                    style={{ width: '100%', paddingBottom: 4, fontWeight: 'bold' }}>
                                    {weekdays.map((weekday, index) => (
                                        <span
                                            className="f-text xs f-date-select__weekday"
                                            key={index}>
                                            {weekday.slice(0, 2)}
                                        </span>
                                    ))}
                                </span>
                            </View>
                        )}
                    />
                </DatePickerProvider>
            </MenuSection>
            <Divider />
            <MenuSection>
                <View
                    row
                    p="0.5rem 0 0 0"
                    gap={6}
                    justifyContent="flex-start"
                    width="100%">
                    <Toggle
                        id="f-date-select-range"
                        onChange={dateRange.check}
                        on={dateRange.checked}
                    />
                    <Label htmlFor="f-date-select-range">Date range</Label>
                </View>
            </MenuSection>
        </Menu>
    )
}
